// Detail page: monitor meta + run history + subdomain findings.
// On mount we POST /view to clear the badge for this user.
// Findings are sorted newest-first; ones discovered within the last 24h get a
// "NEW" banner. The is_new_since_view flag (computed server-side from this
// user's last viewed_at) bubbles those rows to the top.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Clock, ExternalLink, Globe, RefreshCw, Sparkles } from 'lucide-react';
import { subdomainMonitorsAPI } from '../../services/api';
import { useSubdomainMonitorBadge } from '../../contexts/SubdomainMonitorBadgeContext';
import { parseServerDate, formatAbsoluteDate } from '../../utils/time';

const SCHEDULE_LABEL = {
  hourly: 'Hourly',
  every_6h: 'Every 6h',
  daily: 'Daily',
  weekly: 'Weekly',
};

function fmt(ts) {
  return ts ? formatAbsoluteDate(ts) : '-';
}

function relativeTime(ts) {
  if (!ts) return '';
  const d = parseServerDate(ts);
  if (!d || isNaN(d.getTime())) return '';
  const diff = Math.max(0, Date.now() - d.getTime());
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const dd = Math.floor(h / 24);
  return `${dd}d ago`;
}

export default function SubdomainMonitorDetail() {
  const { id } = useParams();
  const monitorId = Number(id);
  const navigate = useNavigate();
  const { refresh: refreshBadge } = useSubdomainMonitorBadge();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [running, setRunning] = useState(false);

  const fetchDetail = useCallback(async () => {
    try {
      const resp = await subdomainMonitorsAPI.get(monitorId);
      setData(resp);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [monitorId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // Mark viewed once the page has loaded its initial payload, then refresh the
  // sidebar badge. We do this AFTER the first detail fetch so the
  // is_new_since_view flags reflect what was new at the moment of click.
  useEffect(() => {
    if (!data) return;
    let cancelled = false;
    (async () => {
      try {
        await subdomainMonitorsAPI.view(monitorId);
        if (!cancelled) refreshBadge();
      } catch (_) {
        // non-critical
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id, monitorId]);

  const handleRunNow = async () => {
    setRunning(true);
    try {
      await subdomainMonitorsAPI.runNow(monitorId);
      await fetchDetail();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setRunning(false);
    }
  };

  const filteredFindings = useMemo(() => {
    if (!data) return [];
    const q = filter.trim().toLowerCase();
    let rows = data.findings || [];
    if (q) {
      rows = rows.filter((f) => {
        const sources = (f.sources || []).join(' ').toLowerCase();
        return f.fqdn.toLowerCase().includes(q) || sources.includes(q);
      });
    }
    // Sort: most recently discovered first.
    return [...rows].sort((a, b) => new Date(b.first_seen_at) - new Date(a.first_seen_at));
  }, [data, filter]);

  if (loading) return <div className="p-6 text-slate-500">Loading monitor...</div>;
  if (error && !data) {
    return (
      <div className="p-6">
        <Link to="/subdomain-monitors" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft className="w-4 h-4" />
          All monitors
        </Link>
        <div className="mt-4 rounded-lg border border-red-300 bg-red-100 px-3 py-2 text-sm text-red-700">{error}</div>
      </div>
    );
  }

  const m = data;

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between">
        <Link to="/subdomain-monitors" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft className="w-4 h-4" />
          All monitors
        </Link>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleRunNow}
            disabled={running || m.status !== 'active'}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-cyan-100 text-cyan-700 border border-cyan-300 hover:bg-cyan-500/25 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${running ? 'animate-spin' : ''}`} />
            {running ? 'Triggering...' : 'Run now'}
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-1">Subdomain Monitor</div>
            <h1 className="text-xl font-semibold text-slate-900 break-all flex items-center gap-2">
              <Globe className="w-5 h-5 text-shinobi-green" />
              {m.root_domain}
            </h1>
            {m.label && <div className="text-sm text-slate-500 mt-1">{m.label}</div>}
          </div>
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs uppercase tracking-wider border ${
            m.status === 'active'
              ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
              : 'bg-slate-100 text-slate-700 border-slate-300'
          }`}>
            {m.status}
          </span>
        </div>

        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <Stat label="Schedule" value={SCHEDULE_LABEL[m.schedule_kind] || m.schedule_kind} />
          <Stat label="Findings" value={m.finding_count} mono />
          <Stat label="Last run" value={fmt(m.last_run_at)} />
          <Stat label="Next run" value={fmt(m.next_run_at)} />
        </div>

        {m.tools && m.tools.length > 0 && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Tools</span>
            {m.tools.map((t) => <SourceChip key={t} source={t} />)}
            {m.tools.includes('dnsx') && (
              <span className="text-[10px] text-slate-500">wordlist: {m.wordlist_size === 'top_110k' ? '110k' : '5k'}</span>
            )}
          </div>
        )}
      </div>

      {(() => {
        const runs = m.runs || [];
        const runTime = (r) => new Date(r.completed_at || r.started_at || 0).getTime();
        const latestRun = runs.length > 0
          ? [...runs].sort((a, b) => runTime(b) - runTime(a))[0]
          : null;
        if (!latestRun) {
          return (
            <div className="text-xs text-slate-500">No scans yet. The first run will appear here once it completes.</div>
          );
        }
        const ts = latestRun.completed_at || latestRun.started_at;
        const statusColor =
          latestRun.status === 'completed' ? 'text-shinobi-green' :
          latestRun.status === 'failed' ? 'text-red-700' :
          latestRun.status === 'running' ? 'text-cyan-700 animate-pulse' :
          'text-slate-500';
        return (
          <div className="text-xs text-slate-500 flex items-center flex-wrap gap-x-2 gap-y-1">
            <span>Last scan: <span className="text-slate-800">{relativeTime(ts)}</span></span>
            <span className="text-slate-600">·</span>
            <span className={statusColor}>{latestRun.status}</span>
            {latestRun.new_findings_count > 0 && (
              <>
                <span className="text-slate-600">·</span>
                <span className="text-shinobi-green font-semibold">+{latestRun.new_findings_count} new findings</span>
              </>
            )}
          </div>
        );
      })()}

      {data.shodan_unavailable && (
        <div className="rounded-lg border border-amber-300 bg-amber-100 px-3 py-2 text-sm text-amber-700">
          Shodan was selected for this monitor but the beacon couldn't run it on the last execution
          — usually because <span className="font-mono">SHODAN_API_KEY</span> isn't set in <span className="font-mono">/etc/shinobi-beacon/env</span>.
          Set it and the next run will include Shodan results.
        </div>
      )}

      <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 overflow-hidden">
        <div className="px-4 py-3 border-b border-cyber-300/30 flex items-center gap-3">
          <div className="text-xs uppercase tracking-wider text-slate-500 flex-shrink-0">
            Subdomains
          </div>
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter FQDNs..."
            className="ml-auto w-64 px-3 py-1.5 text-xs bg-cyber-100 border border-cyber-300/60 rounded-lg text-slate-900 placeholder-slate-500 outline-none focus:border-shinobi-green/70"
          />
        </div>
        {filteredFindings.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            {data.finding_count === 0
              ? 'No subdomains discovered yet. Wait for the first run to complete.'
              : 'No FQDNs match the filter.'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-cyber-200/40 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2">FQDN</th>
                <th className="text-left px-4 py-2">Sources</th>
                <th className="text-left px-4 py-2 w-44">First seen</th>
                <th className="text-left px-4 py-2 w-44">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {filteredFindings.map((f) => (
                <tr key={f.id} className="border-t border-cyber-300/30 hover:bg-cyber-200/20">
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <a
                        href={`https://${f.fqdn}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-shinobi-green hover:underline inline-flex items-center gap-1 break-all"
                      >
                        {f.fqdn}
                        <ExternalLink className="w-3 h-3 opacity-60" />
                      </a>
                      {f.is_within_24h && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-gradient-to-r from-shinobi-green/30 to-cyan-400/30 text-shinobi-green border border-wasabi-300">
                          <Sparkles className="w-2.5 h-2.5" />
                          New
                        </span>
                      )}
                    </div>
                    {f.ip && <div className="text-[11px] text-slate-500 font-mono mt-0.5">{f.ip}</div>}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(f.sources || []).map((src) => <SourceChip key={src} source={src} />)}
                      {(!f.sources || f.sources.length === 0) && <span className="text-slate-500 text-xs italic">-</span>}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap">
                    {fmt(f.first_seen_at)} <span className="text-slate-500">({relativeTime(f.first_seen_at)})</span>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap">
                    {fmt(f.last_seen_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-[11px] text-slate-500 px-1">
        Counts can vary slightly between runs because passive sources (subfinder, Shodan, amass) have different availability windows. Subdomains accumulate across runs — anything new gets the green NEW pill for 24h.
      </p>
    </div>
  );
}

const SOURCE_STYLE = {
  subfinder: 'bg-cyan-100 text-cyan-700 border-cyan-300',
  shodan: 'bg-purple-100 text-purple-700 border-purple-300',
  amass: 'bg-amber-100 text-amber-700 border-amber-300',
  dnsx: 'bg-rose-100 text-rose-700 border-rose-300',
};

function SourceChip({ source }) {
  // sources look like "subfinder:thc" or "shodan" — split on ":" for the base tool color.
  const base = String(source).split(':')[0];
  const cls = SOURCE_STYLE[base] || 'bg-slate-100 text-slate-700 border-slate-300';
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-mono border ${cls}`}>
      {source}
    </span>
  );
}

function Stat({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-slate-800 ${mono ? 'font-mono' : ''}`}>{value}</div>
    </div>
  );
}
